import FVector from '@data/geometry/FVector.js';
import { PipelineExecutionResult } from '@data/Simulations/pipeline/Pipeline';
import { SensorDataManager } from '@data/Simulations/RawSensorDatas';
import type { MixerElement } from '@visualizer/types';
import { PipelinePhase } from '@visualizer/types';
import { getGrid } from 'store/LoadingStore';
import * as THREE from 'three';
import Grid from '../Geodata/grid';
import { grid } from '../Global/global_variables.js';
import usePersistentStore, { addLayer, getGlobalState } from '../Gui/store/persistent/persistentStore.js';
import { Frame } from './Frame';
import Layer from './Layer.js';
import { getDefaultVectorialScalarMode } from './SimConfig';

export type InternalMixerElement = MixerElement & {
    readonly mixedLayerPipelineResult: PipelineExecutionResult; // Result WITHOUT Post blocks (mixer)
    readonly layerPipelineResult: PipelineExecutionResult; // Result WITH Post Blocks (multi)
    updatedLayerPipelineResult: PipelineExecutionResult;
};

export default class LayerMixed extends Layer {
    name: string = 'Mixed Layer';
    parents: Map<string, InternalMixerElement> = new Map();
    isAndLogic: boolean = true;
    protected readonly simKind = 256;
    private nextMultiColorIndex = 0;

    constructor(mixerElements?: MixerElement[]) {
        const activeSimulationSet = getGrid().getActiveLayer()?.parentSimulationSet ?? grid.geodatas[0]?.simulationSets[0];
        super(activeSimulationSet, 'mixed');
        if (this.isNewLayerState) {
            usePersistentStore.getState().updateLayerState(this.getUID(), {
                VECTORIAL_SCALAR_MODE: getDefaultVectorialScalarMode(this.getSimKind()),
            });
        }
        // hardcoded default for safety
        this.fromJSON(mixerElements);
        const actualFrame = new Frame(this, 'Mixed Frame', new SensorDataManager(new Float32Array(), []));

        const result = {
            unitOfMeasure: 'Normalized',
            resultLabel: 'mix',
            resultId: 0,
            frameCollection: [actualFrame],
            resultDomain: {
                min: -Infinity,
                max: Infinity,
            },
        };
        this.results.push(result);

        addLayer(this.UID);
    }

    public refreshInternalState() {
        Array.from(this.parents.entries()).forEach(([key, parent]) => {
            const layer = grid.searchLayerByUID(key);
            const mixedLayerPipelineResult = layer.runPipeline({
                suppressNotifications: true,
                capturePhase: PipelinePhase.Regular,
                octTree: getGrid().octTree!,
            });
            const pipelineResult = layer.runPipeline({ suppressNotifications: true, octTree: getGrid().octTree! });

            // Mutate existing object, to Keep the references of internalMixerElements (multidraw) in instanceRenderer valid:
            Object.assign(parent, {
                mixedLayerPipelineResult,
                layerPipelineResult: pipelineResult,
                updatedLayerPipelineResult: pipelineResult,
            });
        });
        this.applyMultimodeFilter();
    }

    public getInternalMixerElements(): InternalMixerElement[] {
        return Array.from(this.parents.values());
    }

    public addLayer(layerId: string): InternalMixerElement | undefined {
        if (this.parents.has(layerId)) {
            return undefined;
        }
        const colorIndex = this.nextMultiColorIndex;
        const multiColor = getMultiColorByIndex(colorIndex);
        this.nextMultiColorIndex += 1;
        const layer = grid.searchLayerByUID(layerId);
        const mixedLayerPipelineResult = layer.runPipeline({
            suppressNotifications: true,
            capturePhase: PipelinePhase.Regular,
            octTree: getGrid().octTree!,
        });
        const pipelineResult = layer.runPipeline({ suppressNotifications: true, octTree: getGrid().octTree! });
        this.parents.set(layerId, {
            layerId,
            mixedLayerPipelineResult,
            layerPipelineResult: pipelineResult,
            updatedLayerPipelineResult: pipelineResult,
            weight: this.parents.size === 0 ? 1 : 1 / this.parents.size,
            locked: false,
            visible: true,
            invert: false,
            isRangeNormalization: false,
            frameDrawOptions: {
                transparent: false,
                transparencyAmount: 0.5,
                sensorsMultiColor: multiColor.getHex(),
                showSensors: true,
            },
        });
        const updatedLayers = rebalanceMixerWeights(Array.from(this.parents.values()));

        // Apply updated weights back into the parents map without clearing original entries
        this.updateParents(updatedLayers);
        return this.parents.get(layerId);
    }

    public removeLayer(layerId: string) {
        this.parents.delete(layerId);
        this.changeWeight(Array.from(this.parents.values())[0]?.layerId, Array.from(this.parents.values())[0]?.weight);
    }

    toggleLock(layerId: string) {
        const element = this.parents.get(layerId);
        if (element) element.locked = !element.locked;
    }

    toggleVisible(layerId: string) {
        const element = this.parents.get(layerId);
        if (element) element.frameDrawOptions.showSensors = !element.frameDrawOptions.showSensors;
    }
    toggleTransparency(layerId: string) {
        const element = this.parents.get(layerId);
        if (element && element.frameDrawOptions) element.frameDrawOptions.transparent = !element.frameDrawOptions?.transparent;
    }

    setTransparencyAmount(amount: number, opts?: { onlyTransparent?: boolean }) {
        const clamped = Math.max(0, Math.min(1, amount));
        const onlyTransparent = opts?.onlyTransparent ?? false;
        this.parents.forEach((element) => {
            if (!element.frameDrawOptions) return;
            if (onlyTransparent && !element.frameDrawOptions.transparent) return;
            element.frameDrawOptions.transparencyAmount = clamped;
        });
    }

    setLayerTransparencyAmount(layerId: string, amount: number) {
        const element = this.parents.get(layerId);
        if (!element?.frameDrawOptions) return;
        element.frameDrawOptions.transparencyAmount = Math.max(0, Math.min(1, amount));
    }

    toggleInvert(layerId: string) {
        const element = this.parents.get(layerId);
        if (element) element.invert = !element.invert;
    }

    toggleZeroOneNormalization(layerId: string) {
        const element = this.parents.get(layerId);
        if (element) element.isRangeNormalization = !element.isRangeNormalization;
    }
    resetWeights() {
        const unlocked = Array.from(this.parents.values()).filter((el) => {
            return !el.locked;
        });
        const unlockedSum = unlocked.reduce((sum, el) => sum + Math.abs(el.weight), 0);
        if (unlocked.length === 0 || unlockedSum === 0) return;
        const updated = Array.from(this.parents.values()).map((el) => (el.locked ? el : { ...el, weight: unlockedSum / unlocked.length }));
        this.updateParents(updated);
    }

    private updateParents(updatedLayers: MixerElement[]) {
        for (const updatedEl of updatedLayers) {
            const original = this.parents.get(updatedEl.layerId);
            if (original) {
                Object.assign(original, updatedEl);
            }
        }
    }
    public changeWeight(layerId: string, value: number) {
        const clampedValue = Math.max(0, Math.min(1, value as number));
        // Calculate total weight of locked elements (excluding the one being changed)
        const lockedWeight = Array.from(this.parents.values())
            .filter((el) => el.locked && el.layerId !== layerId)
            .reduce((sum, el) => sum + el.weight, 0);
        // Check if the new weight would exceed available space
        const maxAllowedWeight = 1 - lockedWeight;
        const finalValue = Math.min(clampedValue, maxAllowedWeight);
        // Update the element with the new weight value
        const updated = Array.from(this.parents.values()).map((el) => (el.layerId === layerId ? { ...el, weight: finalValue } : el));
        const number_of_unlocked_layers = updated.filter((el) => !el.locked).length;
        const fixedLayerId = number_of_unlocked_layers === 1 ? undefined : layerId; // if only one layer is unlocked, we don't need to rebalance
        const updatedLayers = rebalanceMixerWeights(updated, fixedLayerId);

        // Apply updated weights back into the parents map without clearing original entries
        this.updateParents(updatedLayers);
    }

    // Main Mixer Function (Mixing & And/Or filtering):
    public mixLayers(isAndLogic: boolean) {
        // Remove parents with weight zero
        const parents = Array.from(this.parents.entries())
            .filter(([_, content]) => content.weight !== 0)
            .flatMap(([_, content]) => {
                return content;
            });

        // Init mixer matrix
        let scalarsMatrix = Array.from({ length: parents.length }, () => new Array(Grid.sensorPointsN).fill(null));
        let directionsMatrix = Array.from({ length: parents.length }, () => new Array(Grid.sensorPointsN).fill(null));

        // Fill matrix with legacy values
        for (let i = 0; i < parents.length; i++) {
            const sensorDatas = parents[i].mixedLayerPipelineResult.data;
            for (let j = 0; j < sensorDatas.length; j++) {
                const sensorData = sensorDatas[j];
                const sdIndex = sensorData.getSensorPoint().getID(); // I do this because the arrays are sparse
                directionsMatrix[i][sdIndex] = sensorData.getVectorialResult();
                scalarsMatrix[i][sdIndex] = sensorData.getScalarValue();
            }
        }

        // Map all scalar and directions into [0,1]
        for (let i = 0; i < parents.length; i++) {
            scalarsMatrix[i] = this.normScalarArray(scalarsMatrix[i], parents[i].invert, parents[i].isRangeNormalization);
            directionsMatrix[i] = this.normVectorialArray(directionsMatrix[i], parents[i].invert, parents[i].isRangeNormalization);
        }

        const newScalars = [];
        const newDirections = [];

        let minValue = Infinity;
        let maxValue = -Infinity;
        for (let i = 0; i < Grid.sensorPointsN; i++) {
            let tmpScalar = 0;
            let tmpDir = new FVector();

            newScalars[i] = 0; // You can put here -1 to identify empty mixed sensors
            newDirections[i] = new FVector();

            let breakCicle = false;
            for (let j = 0; j < parents.length; j++) {
                if ((directionsMatrix[j][i] == null || scalarsMatrix[j][i] == null) && isAndLogic) {
                    // IF AND logic break and leave the value to 0
                    breakCicle = true;
                    break;
                } else if ((directionsMatrix[j][i] == null || scalarsMatrix[j][i] == null) && !isAndLogic) {
                    // IF OR logic continue to next parent layer
                    continue;
                }
                const weight = parents[j].weight;
                tmpScalar += scalarsMatrix[j][i] * weight;
                tmpDir = FVector.add(tmpDir, FVector.mult(directionsMatrix[j][i], weight));
            }
            if (breakCicle) continue;
            if (tmpScalar < minValue) minValue = tmpScalar;
            if (tmpScalar > maxValue) maxValue = tmpScalar;
            newScalars[i] = tmpScalar;
            newDirections[i] = tmpDir;
        }

        // console.log(newScalars,newDirections)
        this.getActiveFrame().forceSensorPointValues(newScalars, newDirections);
    }

    // =====================================
    // MULTIMODE
    // =====================================

    // Main Multimode function (applies and/or + highest filter);
    public applyMultimodeFilter() {
        const internalMixerElements = this.getInternalMixerElements();

        const isAndLogic = getGlobalState().data.mixerAndLogic;
        const isHigherLogic = getGlobalState().data.mixerHigherLogic;
        const inclusionSensorIDs = this.computeAndOrInclusionSensorIDs(internalMixerElements, isAndLogic);
        const higherDominantLayerBySensorID = this.computeHigherDominantLayerBySensorID(
            internalMixerElements,
            isHigherLogic,
            inclusionSensorIDs,
            isAndLogic
        );

        internalMixerElements.forEach((element) => {
            const sourceResult = element.layerPipelineResult;
            element.updatedLayerPipelineResult = {
                ...sourceResult,
                data: sourceResult.data.filter((sensorData) =>
                    this.shouldKeepSensorInFilteredResult(
                        sensorData.getID(),
                        sensorData.getScalarValue(),
                        element.layerId,
                        isHigherLogic,
                        inclusionSensorIDs,
                        isAndLogic,
                        higherDominantLayerBySensorID
                    )
                ),
            };
        });
    }

    // MultiMode AND / OR Logic:
    private computeAndOrInclusionSensorIDs(internalMixerElements: InternalMixerElement[], isAndLogic: boolean): Set<number> {
        if (!isAndLogic || internalMixerElements.length === 0) return new Set<number>();

        return internalMixerElements
            .map(
                (element) =>
                    new Set(
                        element.layerPipelineResult.data
                            .filter((sensorData) => sensorData.getScalarValue() !== 0)
                            .map((sensorData) => sensorData.getID())
                    )
            )
            .reduce((acc, curr) => new Set([...acc].filter((id) => curr.has(id))));
    }

    // Multimode Higher logic:
    private computeHigherDominantLayerBySensorID(
        internalMixerElements: InternalMixerElement[],
        isHigherLogic: boolean,
        inclusionSensorIDs: Set<number>,
        isAndLogic: boolean
    ): Map<number, string> {
        const higherDominantLayerBySensorID = new Map<number, string>();
        if (!isHigherLogic) return higherDominantLayerBySensorID;

        const dominantBySensorID = new Map<number, { score: number; layerId: string }>();

        internalMixerElements.forEach((element) => {
            if (!element.frameDrawOptions.showSensors) return;

            const layerId = element.layerId;
            const [mapMinValue, mapMaxValue] = element.layerPipelineResult.executionContext.remappedMinMax;

            element.layerPipelineResult.data
                .filter((sensorData) => sensorData.getScalarValue() !== 0)
                .filter((sensorData) => !isAndLogic || inclusionSensorIDs.has(sensorData.getID()))
                .forEach((sensorData) => {
                    // Compute mapped value for the sensor with this id, in this layer:
                    const id = sensorData.getID();
                    const mappedValue = this.computeMappedSensorValue(sensorData.getScalarValue(), mapMinValue, mapMaxValue);
                    if (!Number.isFinite(mappedValue)) return;

                    // Update dominant sensor point if the sensor point of the current layer has an higher mapped value:
                    const current = dominantBySensorID.get(id);
                    if (!current || mappedValue > current.score) {
                        dominantBySensorID.set(id, { score: mappedValue, layerId });
                    }
                });
        });

        dominantBySensorID.forEach((entry, sensorID) => {
            higherDominantLayerBySensorID.set(sensorID, entry.layerId);
        });

        return higherDominantLayerBySensorID;
    }

    private shouldKeepSensorInFilteredResult(
        sensorId: number,
        sensorValue: number,
        layerId: string,
        isHigherMode: boolean,
        inclusionSensorIDs: ReadonlySet<number>,
        isAndLogic: boolean,
        higherDominantLayerBySensorID: ReadonlyMap<number, string>
    ): boolean {
        if (sensorValue === 0) return true;
        if (isAndLogic && !inclusionSensorIDs.has(sensorId)) return false;
        if (isHigherMode && higherDominantLayerBySensorID.get(sensorId) !== layerId) return false;
        return true;
    }

    private computeMappedSensorValue(value: number, minValue: number, maxValue: number) {
        if (!Number.isFinite(value)) return Number.NaN;
        if (maxValue === minValue) return value > 0 ? 1 : 0;
        return Math.max(0, Math.min(1, (value - minValue) / (maxValue - minValue)));
    }

    private normScalarArray(arr: number[], invert = false, isRangeNormalization = true) {
        // Do not use spread operators (might overflow stack with large sensors array):
        let max = -Infinity;
        let min = Infinity;

        for (const value of arr) {
            if (value > max) max = value;
            if (value < min) min = value;
        }

        if (max == min) return arr.map(() => 1);
        // Handle edge case: all values are the same
        const mapFunction = isRangeNormalization ? (value: number) => (value - min) / (max - min) : (value: number) => value / max;
        // Normalize and optionally invert the values
        return arr.map((value) => {
            if (!value) return null;
            const mappedValue = mapFunction(value);
            return invert ? 1 - mappedValue : mappedValue;
        });
    }

    private normVectorialArray(array: FVector[], invert = false, isRangeNormalization = true) {
        // Step 1: Convert to magnitudes, skipping invalid values
        const magnitudes = array.map((vector) => (vector ? vector.mag() : null));

        // Filter valid magnitudes for min/max calculation
        const filteredMagnitudes: number[] = magnitudes
            .filter((mag): mag is number => mag !== null)
            .filter((mag) => mag > 0 && mag !== Infinity);

        if (filteredMagnitudes.length === 0) {
            //FAILED("The last layer wasn't valid for the mixer. Please remove it and try again.");
            const fallbackVector = null; // new FVector(1, 1, 1).normalize()
            return array.map(() => fallbackVector);
        }

        let maxMag = -Infinity;
        let minMag = Infinity;

        for (const magnitude of filteredMagnitudes) {
            if (magnitude > maxMag) maxMag = magnitude;
            if (magnitude < minMag) minMag = magnitude;
        }

        const mapFunction = isRangeNormalization
            ? (value: number) => (value - minMag) / (maxMag - minMag)
            : (value: number) => value / maxMag;

        if (maxMag == minMag) return array.map((value) => (value ? value.normalize() : null));
        // Avoid division by zero in case all magnitudes are the same

        // Step 2: Normalize and remap
        return array.map((vector) => {
            if (!vector) return null;
            const mag = vector.mag();
            if (mag <= 0 || mag === Infinity) {
                return null; // Return the original vector if invalid magnitude
            }

            const mappedValue = mapFunction(mag);
            const remappedMagnitude = invert ? 1 - mappedValue : mappedValue;

            return vector.normalize().mult(remappedMagnitude);
        });
    }

    public toJSON(): MixerElement[] {
        return Array.from(this.parents.entries()).map(([id, content]) => {
            return {
                layerId: id,
                weight: content.weight,
                locked: content.locked,
                visible: content.visible,
                invert: content.invert,
                isRangeNormalization: content.isRangeNormalization,
                frameDrawOptions: content.frameDrawOptions,
            };
        });
    }

    public fromJSON(mixerElements?: MixerElement[]): void {
        let maxPersistedColorIndex = -1;
        mixerElements?.forEach((element) => {
            const added = this.addLayer(element.layerId);
            if (!added) return;

            // Sync persisted mixer element settings with current runtime:
            added.weight = element.weight ?? added.weight;
            added.locked = element.locked ?? added.locked;
            added.visible = element.visible ?? added.visible;
            added.invert = element.invert ?? added.invert;
            added.isRangeNormalization = element.isRangeNormalization ?? added.isRangeNormalization;
            added.frameDrawOptions = {
                ...added.frameDrawOptions,
                ...(element.frameDrawOptions ?? {}),
            };

            const persistedColor = element.frameDrawOptions?.sensorsMultiColor;
            if (typeof persistedColor === 'number') {
                const colorIndex = getMultiColorIndexByHex(persistedColor);
                if (colorIndex > maxPersistedColorIndex) {
                    maxPersistedColorIndex = colorIndex;
                }
            }
        });

        // Next multicolor should be after the last loaded layer color, to avoid color duplication:
        this.nextMultiColorIndex = Math.max(mixerElements?.length ?? 0, maxPersistedColorIndex + 1);
    }
}

/**
 * Generates an array of colors for MultiMode.
 * @param numColors Number of colors to generate
 * @returns Array of THREE.Color
 */
function generateMultiColors(numColors: number): THREE.Color[] {
    const basePalette = [0x226e4f, 0x7d6111, 0xac3629, 0x833aa3, 0x2f50b9, 0x296882, 0x9c5011, 0x4a6c24, 0x943e72, 0x63666b];

    const boostedPalette = basePalette.map((hex) => {
        const baseColor = new THREE.Color(hex);
        const hsl = { h: 0, s: 0, l: 0 };
        baseColor.getHSL(hsl);
        return new THREE.Color().setHSL(hsl.h, Math.min(1, hsl.s + 0.2), Math.min(1, hsl.l + 0.3));
    });

    return Array.from({ length: numColors }, (_, i) => boostedPalette[i % boostedPalette.length]);
}

function getMultiColorByIndex(index: number): THREE.Color {
    return generateMultiColors(index + 1)[index];
}

function getMultiColorIndexByHex(hex: number): number {
    const normalizedHex = hex >>> 0;
    const palette = generateMultiColors(10);
    for (let i = 0; i < palette.length; i++) {
        if (palette[i].getHex() >>> 0 === normalizedHex) return i;
    }
    return -1;
}

function rebalanceMixerWeights(elements: MixerElement[], fixedId?: string): MixerElement[] {
    // First, ensure the fixed element's weight is within valid bounds
    const fixedElement = elements.find((el) => el.layerId === fixedId);

    // Calculate total weight of all locked elements (excluding the fixed one if it's locked)
    const lockedElements = elements.filter((el) => el.locked && el.layerId !== fixedId);
    const totalLockedWeight = lockedElements.reduce((sum, el) => sum + el.weight, 0);

    // Get the current weight of the fixed element (the one being dragged)
    const fixedWeight = fixedElement ? fixedElement.weight : 0;

    // Calculate available weight for unlocked elements (excluding fixed)
    const availableWeight = 1 - totalLockedWeight - fixedWeight;

    // Get all unlocked elements that are not the fixed one
    const adjustableElements = elements.filter((el) => !el.locked && el.layerId !== fixedId);

    // If no adjustable elements, just return as-is (this handles edge cases)
    if (adjustableElements.length === 0) {
        return elements;
    }

    // Calculate current total weight of adjustable elements
    const currentAdjustableWeight = adjustableElements.reduce((sum, el) => sum + el.weight, 0);

    // Redistribute the available weight proportionally among adjustable elements
    return elements.map((el) => {
        // Keep locked elements unchanged
        if (el.locked) {
            return el;
        }

        // Keep the fixed element (being dragged) unchanged
        if (el.layerId === fixedId) {
            return el;
        }

        // Redistribute weight for adjustable elements
        if (currentAdjustableWeight > 0 && availableWeight > 0) {
            // Proportional redistribution
            const newWeight = (el.weight / currentAdjustableWeight) * availableWeight;
            return { ...el, weight: Math.max(0, newWeight) };
        } else {
            // Equal distribution if no previous weights or no available weight
            const equalWeight = Math.max(0, availableWeight / adjustableElements.length);
            return { ...el, weight: equalWeight };
        }
    });
}
